import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ChevronDown, FileText, Star, Clock, X, Columns2 } from 'lucide-react';

const slug = (path) => path.replace(/^\//, '').replace(/^__|__$/g, '');

const RecentBar = ({ recent, byPath, onOpen }) => {
  const items = recent.map((p) => byPath[p]).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="flex flex-nowrap md:flex-wrap items-center gap-2 overflow-x-auto pb-1 md:pb-0" data-testid="recent-reports-bar">
      <span className="flex items-center gap-1 text-sm font-semibold text-gray-500 ml-1 shrink-0"><Clock className="w-4 h-4" />אחרונים:</span>
      {items.map((r) => (
        <button
          key={r.path}
          type="button"
          onClick={() => onOpen(r.path)}
          className="shrink-0 whitespace-nowrap px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-indigo-50 text-indigo-700 font-semibold text-sm border border-indigo-200 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors"
          data-testid={`recent-report-${slug(r.path)}`}
        >
          {r.title}
        </button>
      ))}
    </div>
  );
};

const ReportRow = ({ report, isFav, onOpen, onToggleFavorite, prefix }) => (
  <div className="flex items-center gap-1 rounded-md hover:bg-indigo-50 transition-colors">
    <button
      type="button"
      onClick={() => onToggleFavorite(report.path)}
      title={isFav ? 'הסר ממועדפים' : 'הוסף למועדפים'}
      className="p-2 rounded-md hover:scale-125 transition-transform"
      data-testid={`${prefix}favorite-toggle-${slug(report.path)}`}
    >
      <Star className={`w-5 h-5 ${isFav ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
    </button>
    <button type="button" onClick={() => onOpen(report.path)} className="flex-1 text-right py-3 pl-3" data-testid={`${prefix}report-option-${slug(report.path)}`}>
      <span className="font-semibold text-gray-900">{report.title}</span>
      <span className="text-gray-500 text-sm mr-2">— {report.description}</span>
    </button>
  </div>
);

const ReportDropdown = ({ reports, favorites, onOpen, onToggleFavorite, title, placeholder, testId, prefix = '', accent = 'indigo' }) => {
  const [open, setOpen] = useState(false);
  const favs = reports.filter((r) => favorites.includes(r.path));
  const others = reports.filter((r) => !favorites.includes(r.path));
  const pick = (path) => { setOpen(false); onOpen(path); };
  const row = (r, isFav) => <ReportRow key={r.path} report={r} isFav={isFav} onOpen={pick} onToggleFavorite={onToggleFavorite} prefix={prefix} />;
  const border = accent === 'amber' ? 'border-amber-300 hover:border-amber-500' : 'border-indigo-200 hover:border-indigo-400';
  const text = accent === 'amber' ? 'text-amber-700' : 'text-indigo-700';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`flex-1 min-w-0 flex items-center justify-between text-base md:text-lg py-2.5 md:py-3 px-4 rounded-md border-2 bg-white outline-none transition-colors ${border}`} data-testid={testId}>
          {title ? <span className={`font-bold truncate ${text}`} data-testid={`${testId}-title`}>{title}</span> : <span className="text-gray-500 truncate">{placeholder}</span>}
          <ChevronDown className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent dir="rtl" align="start" className="p-2 w-[var(--radix-popover-trigger-width)] min-w-[18rem] max-h-[28rem] overflow-y-auto" data-testid={`${testId}-list`}>
        {favs.length > 0 && (
          <>
            <p className="text-xs font-bold text-amber-600 px-2 pt-1 pb-1" data-testid={`${prefix}favorites-heading`}>מועדפים</p>
            {favs.map((r) => row(r, true))}
            <div className="border-t my-2" />
          </>
        )}
        {others.map((r) => row(r, false))}
      </PopoverContent>
    </Popover>
  );
};

const CloseBtn = ({ onClick, testId, label }) => (
  <button type="button" onClick={onClick} title={label} className="flex items-center gap-1 px-3 md:px-4 py-2.5 md:py-3 rounded-md border-2 border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors shrink-0" data-testid={testId}>
    <X className="w-5 h-5" /><span className="hidden md:inline">{label}</span>
  </button>
);

export const ReportPicker = ({ reports, favorites, recent, onOpen, onToggleFavorite, activeTitle, onClose, compareReports, secondTitle, onOpenSecond, onCloseSecond }) => {
  const [showSecond, setShowSecond] = useState(false);
  useEffect(() => { if (!secondTitle) setShowSecond(false); }, [secondTitle]);
  const byPath = Object.fromEntries(reports.map((r) => [r.path, r]));
  const orderedFavs = favorites.filter((p) => byPath[p]);
  const secondVisible = compareReports && (showSecond || secondTitle);

  return (
    <div className="space-y-3">
      <RecentBar recent={recent} byPath={byPath} onOpen={onOpen} />
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
        <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
          <span className="hidden md:flex text-lg font-bold text-gray-800 items-center gap-2 shrink-0"><FileText className="w-6 h-6 text-indigo-600" />בחר דוח</span>
          <ReportDropdown reports={reports} favorites={orderedFavs} onOpen={onOpen} onToggleFavorite={onToggleFavorite} title={activeTitle} placeholder="בחר דוח מהרשימה..." testId="report-picker-select" />
          {activeTitle && <CloseBtn onClick={onClose} testId="close-report-btn" label="סגור דוח" />}
          {compareReports && !secondVisible && (
            <button type="button" onClick={() => setShowSecond(true)} className="flex items-center gap-2 px-3 md:px-4 py-2.5 md:py-3 rounded-md bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors shrink-0" title="פתח דוח שני להשוואה" data-testid="compare-btn">
              <Columns2 className="w-5 h-5" /><span className="hidden md:inline">פתח דוח שני להשוואה</span>
            </button>
          )}
        </div>
        {secondVisible && (
          <div className="flex items-center gap-2 flex-1 min-w-0" data-testid="second-picker">
            <span className="hidden md:flex text-sm font-bold text-amber-700 items-center gap-1 shrink-0"><Columns2 className="w-5 h-5" />להשוואה</span>
            <ReportDropdown reports={compareReports} favorites={orderedFavs} onOpen={onOpenSecond} onToggleFavorite={onToggleFavorite} title={secondTitle} placeholder="בחר דוח שני..." testId="second-report-select" prefix="second-" accent="amber" />
            <CloseBtn onClick={() => { setShowSecond(false); onCloseSecond(); }} testId="close-second-btn" label="סגור" />
          </div>
        )}
      </div>
    </div>
  );
};
